import React, { useEffect, useLayoutEffect, useState } from 'react';
import Auth from '../../Utils/Auth/auth';
import initLogBehavior from '../../services/LogBehavior/LogBehavior';
import { Card, Button } from '../../ds';
import Amount from './Amount/Amount';
import InfoCard from './InfoCard';
import SelectStores from './SelectStores';
import QRCode from 'react-qr-code';
import {
  StyledBody,
  StyledContainer,
  StyledHeader,
  StyledModal,
  StyledTitle,
  StyleSubTitle,
  SyledImage,
} from '../LatestFeatures/styled';

import './payOrder.scss';
import { useStore } from '../../services/store';
import { observer } from 'mobx-react-lite';
import AnalyticsEvent, {
  AnalyticsEventDictionary,
} from '../../services/analytics/AnalyticsEvent';
import { useRef } from 'react';
import MerchantPaymentsService from '../../services/MerchantPaymentsService';
import { isFeatureEnabled } from '../../Utils/Feature/feature_flag';

const initialPaymentData = {
  isModalOpen: false,
  paymentId: '',
  orderId: '',
  errors: '',
  isValid: false,
  amount: 0,
  tokenErrors: '',
  path: '',
  token: '',
};

const PayOrderByQR = () => {
  const isTracked = useRef(false);
  const user = Auth.userData();
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [code, setCode] = useState('');
  const [url, setUrl] = useState('');
  const [error, setError] = useState('');
  const [hasQr, setHasQr] = useState(false);

  if (!user) {
    return '';
  }

  const {
    qrCodeStore: { storeId, setStoreId, stores, selectedStore },
  } = useStore();
  const userLogBehavior = React.useRef('');
  const [location, setLocation] = useState({});
  const [paymentData, setPaymentData] = useState(initialPaymentData);

  const mainRole =
    user?.mainRole || (user?.roles?.length ? user.roles[0] : undefined);

  const canSelectStore =
    mainRole === 'general_admin' || mainRole === 'store_admin';

  useLayoutEffect(() => {
    if (!isTracked.current) {
      AnalyticsEvent().inStore({
        eventId: AnalyticsEventDictionary.chargeQrViewLoaded,
      });
      isTracked.current = true;
    }
    initLogBehavior(userLogBehavior);
  }, []);

  useEffect(() => {
    let storeToFetch = null;
    if (canSelectStore) {
      if (!selectedStore) return;
      storeToFetch = selectedStore;
    } else {
      if (!storeId) return;
      storeToFetch = storeId;
    }
    if (!storeToFetch) return;

    const fetchStaticQrCode = async () => {
      try {
        const response =
          await MerchantPaymentsService().getStaticCodeByStore(storeToFetch);
        if (!response.url) {
          setHasQr(false);
          setError('No se pudo obtener el código QR para esta sucursal.');
          setCode('');
          setUrl('');
          return;
        }
        setCode(response.code);
        setUrl(response.url);
        setHasQr(true);
      } catch (err) {
        setHasQr(false);
        setCode('');
        setUrl('');
      }
    };
    fetchStaticQrCode();
  }, [storeId, selectedStore, canSelectStore]);

  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        position => {
          setLocation({
            lat: position.coords.latitude,
            lon: position.coords.longitude,
          });
        },
        error => {
          console.error('Error getting location:', error.message);
        }
      );
    }
  }, []);

  const resetPaymentData = () => {
    setPaymentData(initialPaymentData);
  };

  return (
    <>
      <div className='k-ds-width-full pay-order'>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '28px',
          }}
        >
          <div className='pay-order-header k-ds-m-bottom-sp-07'>
            Cobrar por Código QR
          </div>
          {isFeatureEnabled('instore_qr_static_code_sales') && (
            <div>
              {((selectedStore && hasQr) || (storeId && hasQr)) && (
                <Button
                  type='button'
                  className='k-ds-m-right-sp-02'
                  horizontalPadding='22px'
                  verticalPadding='16px'
                  size='small'
                  onClick={() => {
                    setIsOpenModal(!isOpenModal);
                  }}
                >
                  <span style={{ width: '220px' }}>
                    Mostrar código QR estático
                  </span>
                </Button>
              )}
            </div>
          )}
        </div>
        <InfoCard />
        {stores.length > 0 && (
          <Card type='secondary' className='k-ds-width-full'>
            <div className='sub-header k-ds-m-bottom-sp-05'>Sucursal</div>
            <div>
              <SelectStores
                label='Selecciona una sucursal'
                placeholder='Elige una opción o escribe aquí'
                helper='El cobro se asignará a esta sucursal.'
                required
                name='sucursal'
                stores={stores}
                value={selectedStore}
                onChange={option => (option ? setStoreId(option.value) : null)}
              />
            </div>
          </Card>
        )}
        <Amount
          storeid={storeId}
          location={location}
          logbehavior={userLogBehavior}
          paymentAmount={paymentData.amount}
          isPaymentValidated={paymentData.isValid}
          onClear={resetPaymentData}
        />
      </div>
      {isOpenModal && isFeatureEnabled('instore_qr_static_code_sales') && (
        <StyledModal
          isOpen={isOpenModal}
          onClose={() => setIsOpenModal(false)}
          onOverlayClick={() => setIsOpenModal(false)}
          showClose
          theme='dark'
          cardType='secondary'
        >
          <StyledContainer>
            <StyledHeader>Escanea el código QR</StyledHeader>
            <StyledBody
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <StyledTitle>Sigue las instrucciones en tu celular</StyledTitle>
              <SyledImage>
                <QRCode value={url} size={200} />
              </SyledImage>
              <StyleSubTitle>{code}</StyleSubTitle>
            </StyledBody>
          </StyledContainer>
        </StyledModal>
      )}
      {error && (
        <StyledModal
          isOpen={!!error}
          onClose={() => setError('')}
          onOverlayClick={() => setError('')}
          showClose
          theme='dark'
          cardType='secondary'
        >
          <StyledContainer>
            <StyledHeader>Error</StyledHeader>
            <StyledBody>
              <StyledTitle>{error}</StyledTitle>
              <Button onClick={() => setError('')}>Cerrar</Button>
            </StyledBody>
          </StyledContainer>
        </StyledModal>
      )}
    </>
  );
};

export default observer(PayOrderByQR);
